import { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Drawer from '@mui/material/Drawer';
import { Close } from '@mui/icons-material';
import styled from '@emotion/styled';
import { v4 as uuidv4 } from 'uuid';
import type { NewsI, NewsDraftI } from 'types';
import { createNews, updateNews } from 'Api/news-api';
import { s3 } from 'Services/AWS/s3';
import { settingsTheme as t } from '../../theme';
import { Button, FormField, TextInput, SegmentedControl } from '../../Instructors/components';
import { newsFormSchema, NewsFormValues, isoToLocalInput, localInputToIso } from '../schemas/news-form-schema';
import DateTimeInput from './form/DateTimeInput';
import RichTextEditor from './form/RichTextEditor';
import ImageDropzone from './form/ImageDropzone';

export interface NewsFormLabels {
  title: string;
  subtitle: string;
  close: string;
  fieldTitle: string;
  titlePlaceholder: string;
  fieldDescription: string;
  descriptionPlaceholder: string;
  /** aria-label del botón de negritas de la toolbar del editor. */
  descriptionBold: string;
  /** aria-label del botón de cursiva de la toolbar del editor. */
  descriptionItalic: string;
  /** aria-label del botón de encabezado 1 de la toolbar del editor. */
  descriptionHeading1: string;
  /** aria-label del botón de encabezado 2 de la toolbar del editor. */
  descriptionHeading2: string;
  /** aria-label del botón de lista con viñetas de la toolbar del editor. */
  descriptionBulletList: string;
  /** aria-label del botón de lista numerada de la toolbar del editor. */
  descriptionOrderedList: string;
  /** Bloque "texto normal" del menú de slash del editor. */
  descriptionParagraph: string;
  /** aria-label de la toolbar fija del editor. */
  descriptionToolbar: string;
  /** aria-label de la barra flotante sobre la selección. */
  descriptionSelectionToolbar: string;
  /** aria-label del menú de slash ("/"). */
  descriptionSlashMenu: string;
  fieldSchedule: string;
  /** Segmento "publicar ahora" del toggle de programación. */
  scheduleNow: string;
  /** Segmento "programar" del toggle de programación. */
  scheduleLater: string;
  /** Aviso bajo el toggle apagado: la noticia se publica al guardar. */
  scheduleHint: string;
  fieldPublishAt: string;
  fieldExpiresAt: string;
  fieldPinned: string;
  pinnedYes: string;
  pinnedNo: string;
  fieldImage: string;
  imageDrop: string;
  imageHint: string;
  imageRemove: string;
  imageTooLarge: string;
  imageInvalidType: string;
  /** La noticia ya se guardó pero la imagen no subió; reintentar no duplica. */
  imageUploadFailed: string;
  genericError: string;
  cancel: string;
  save: string;
}

interface NewsFormDrawerProps {
  open: boolean;
  /** Noticia en edición, o `null` para alta. */
  editing: NewsI | null;
  labels: NewsFormLabels;
  onClose: () => void;
  /** Éxito completo: la página cierra, refresca y muestra el toast. */
  onSaved: (mode: 'created' | 'updated') => void;
  /**
   * La noticia se creó pero la imagen falló: la lista cambió aunque el drawer
   * siga abierto mostrando el error, así que la página debe refrescar.
   */
  onListChanged?: () => void;
}

const emptyValues: NewsFormValues = {
  title: '',
  description: '',
  scheduled: false,
  publishAt: '',
  pinned: false,
  expiresAt: '',
};

const toFormValues = (news: NewsI | null): NewsFormValues =>
  news
    ? {
        title: news.title,
        description: news.description,
        // El toggle arranca encendido solo si la noticia sigue Programada
        // (publishAt en el futuro al abrir el drawer).
        scheduled: new Date(news.publishAt).getTime() > Date.now(),
        publishAt: isoToLocalInput(news.publishAt),
        pinned: news.pinned,
        expiresAt: isoToLocalInput(news.expiresAt),
      }
    : emptyValues;

/** Key S3 del contrato: `news/{id}/{uuid}.{ext}`; la extensión sale del nombre del archivo. */
const buildImageKey = (newsId: string, file: File): string => {
  const fromName = file.name.includes('.') ? file.name.split('.').pop() : null;
  const extension = (fromName || file.type.split('/')[1] || 'jpg').toLowerCase();
  return `news/${newsId}/${uuidv4()}.${extension}`;
};

const Panel = styled.div({
  width: 480,
  maxWidth: '92vw',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const Head = styled.div({
  flex: 'none',
  padding: '22px 26px',
  borderBottom: `1px solid ${t.color.rowBorder}`,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
});

const HeadTitle = styled.div({
  fontFamily: t.font,
  fontSize: 18,
  fontWeight: 600,
  color: t.color.title,
});

const HeadSubtitle = styled.div({
  fontFamily: t.font,
  fontSize: 13,
  color: t.color.muted,
  marginTop: 3,
});

const CloseButton = styled.button({
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: t.color.muted,
  display: 'inline-flex',
  '& .MuiSvgIcon-root': { fontSize: 24 },
});

const Body = styled.form({
  flex: 1,
  overflowY: 'auto',
  padding: '24px 26px',
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
});

const FieldPair = styled.div({
  display: 'flex',
  gap: 14,
  '& > *': { flex: 1, minWidth: 0 },
});

const Hint = styled.div({
  fontFamily: t.font,
  fontSize: 12,
  color: t.color.muted,
});

const ServerError = styled.div({
  fontFamily: t.font,
  fontSize: 13,
  color: t.color.danger,
  background: t.color.dangerSurface,
  border: `1px solid ${t.color.danger}`,
  borderRadius: t.radius.sm,
  padding: '10px 13px',
});

const Footer = styled.div({
  flex: 'none',
  borderTop: `1px solid ${t.color.rowBorder}`,
  padding: '18px 26px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
});

/**
 * Organismo: panel lateral de alta y edición de una noticia (calca del drawer
 * de Instructores; RHF + zod). Diferencias de contrato: el guardado va por
 * `PATCH` parcial —nunca PUT— y las fechas se convierten de `datetime-local`
 * a ISO 8601 justo al enviar.
 *
 * Imagen (contrato MEX-464): en alta, `POST` sin imagen → subir a
 * `news/{id}/{uuid}.{ext}` por presigned PUT → `PATCH {imageKey}`. En edición
 * se sube con key nueva y el back borra la anterior al recibir el PATCH. Si la
 * subida falla tras el create, la noticia ya existe: el drawer queda abierto
 * mostrando el error de imagen y el reintento hace PATCH sobre esa noticia,
 * sin duplicar el POST.
 */
const NewsFormDrawer = ({ open, editing, labels, onClose, onSaved, onListChanged }: NewsFormDrawerProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
  } = useForm<NewsFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(newsFormSchema),
    defaultValues: emptyValues,
  });
  // El selector de fecha solo existe mientras el toggle de programación está encendido.
  const scheduled = useWatch({ control, name: 'scheduled' });

  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  // Id de una noticia recién creada cuyo paso de imagen falló: el reintento va
  // por PATCH sobre ella en lugar de repetir el POST.
  const createdIdRef = useRef<string | null>(null);

  // Reinicia el formulario al abrir, según se trate de alta o edición; en
  // edición firma la imagen guardada para el preview.
  useEffect(() => {
    if (!open) return undefined;
    reset(toFormValues(editing));
    setFile(null);
    setServerError(null);
    setCurrentImageUrl(null);
    createdIdRef.current = null;

    if (!editing?.imageKey) return undefined;
    let isMounted = true;
    s3.getSignedUrl(editing.imageKey)
      .then((response: { signedUrl?: string } | null) => {
        if (isMounted) setCurrentImageUrl(response?.signedUrl ?? null);
      })
      .catch(() => {
        // Sin URL firmada solo se pierde el preview; el formulario sigue usable.
      });
    return () => {
      isMounted = false;
    };
    // Solo reaccionamos a la apertura; `editing` se captura en ese instante.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** Sube el archivo y regresa la key, o null dejando el error visible. */
  const uploadImage = async (newsId: string): Promise<string | null> => {
    if (!file) return null;
    const key = buildImageKey(newsId, file);
    try {
      // El JSDoc de s3.js declara Buffer, pero en runtime recibe el File tal
      // cual (va de body del PUT presignado); el cast solo alinea los tipos.
      await s3.uploadFileToBucketWithKeyProvided(key, file as unknown as Buffer, file.type);
      return key;
    } catch {
      setServerError(labels.imageUploadFailed);
      return null;
    }
  };

  const submitCreate = async (values: NewsFormValues) => {
    const draft: NewsDraftI = {
      title: values.title,
      description: values.description.trim(),
      // Sin programación, la fecha efectiva es el momento del envío.
      publishAt: values.scheduled ? localInputToIso(values.publishAt) : new Date().toISOString(),
      pinned: values.pinned,
      ...(values.expiresAt ? { expiresAt: localInputToIso(values.expiresAt) } : {}),
    };
    const created = await createNews(draft);
    if (created.error !== null || created.data === null) {
      setServerError(created.error ?? labels.genericError);
      return;
    }

    if (file) {
      // Desde aquí la noticia ya existe: cualquier tropiezo del paso de imagen
      // deja el drawer abierto en modo reintento y avisa a la lista.
      createdIdRef.current = created.data.id;
      const key = await uploadImage(created.data.id);
      if (key === null) {
        onListChanged?.();
        return;
      }
      const patched = await updateNews(created.data.id, { imageKey: key });
      if (patched.error !== null) {
        setServerError(patched.error);
        onListChanged?.();
        return;
      }
    }
    onSaved('created');
  };

  const submitUpdate = async (targetId: string, values: NewsFormValues) => {
    // PATCH parcial: solo viajan los campos que el usuario tocó.
    const partial: Partial<NewsDraftI> = {};
    if (dirtyFields.title) partial.title = values.title;
    if (dirtyFields.description) partial.description = values.description.trim();
    // El toggle o la fecha tocados deciden el publishAt: programado manda la
    // fecha elegida; apagado publica ya. Si el toggle quedó apagado sin tocarse
    // (noticia ya publicada), la fecha original no se pisa.
    if (dirtyFields.scheduled || dirtyFields.publishAt) {
      partial.publishAt = values.scheduled ? localInputToIso(values.publishAt) : new Date().toISOString();
    }
    if (dirtyFields.pinned) partial.pinned = values.pinned;
    if (dirtyFields.expiresAt) partial.expiresAt = values.expiresAt ? localInputToIso(values.expiresAt) : null;

    if (file) {
      const key = await uploadImage(targetId);
      if (key === null) return;
      // El back borra la imagen anterior de S3 al recibir el imageKey nuevo.
      partial.imageKey = key;
    }

    if (Object.keys(partial).length === 0) {
      onSaved('updated');
      return;
    }
    const updated = await updateNews(targetId, partial);
    if (updated.error !== null) {
      setServerError(updated.error);
      return;
    }
    onSaved('updated');
  };

  const submit = async (values: NewsFormValues) => {
    setSubmitting(true);
    setServerError(null);
    try {
      const targetId = editing?.id ?? createdIdRef.current;
      if (targetId === null || targetId === undefined) await submitCreate(values);
      else await submitUpdate(targetId, values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { boxShadow: '-8px 0 30px rgba(16, 24, 40, 0.18)' } }}
    >
      <Panel>
        <Head>
          <div>
            <HeadTitle>{labels.title}</HeadTitle>
            <HeadSubtitle>{labels.subtitle}</HeadSubtitle>
          </div>
          <CloseButton type="button" onClick={onClose} aria-label={labels.close}>
            <Close />
          </CloseButton>
        </Head>

        <Body id="news-form" onSubmit={handleSubmit(submit)}>
          {serverError ? <ServerError role="alert">{serverError}</ServerError> : null}

          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <FormField label={labels.fieldTitle} error={errors.title?.message}>
                <TextInput
                  value={field.value}
                  placeholder={labels.titlePlaceholder}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  invalid={!!errors.title}
                />
              </FormField>
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <FormField label={labels.fieldDescription} error={errors.description?.message}>
                <RichTextEditor
                  value={field.value}
                  placeholder={labels.descriptionPlaceholder}
                  labels={{
                    bold: labels.descriptionBold,
                    italic: labels.descriptionItalic,
                    heading1: labels.descriptionHeading1,
                    heading2: labels.descriptionHeading2,
                    bulletList: labels.descriptionBulletList,
                    orderedList: labels.descriptionOrderedList,
                    paragraph: labels.descriptionParagraph,
                    toolbar: labels.descriptionToolbar,
                    selectionToolbar: labels.descriptionSelectionToolbar,
                    slashMenu: labels.descriptionSlashMenu,
                  }}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              </FormField>
            )}
          />

          <Controller
            name="scheduled"
            control={control}
            render={({ field }) => (
              <FormField label={labels.fieldSchedule}>
                <SegmentedControl<'now' | 'later'>
                  value={field.value ? 'later' : 'now'}
                  onChange={value => field.onChange(value === 'later')}
                  options={[
                    { value: 'now', label: labels.scheduleNow, activeColor: t.color.muted },
                    { value: 'later', label: labels.scheduleLater },
                  ]}
                />
                {field.value ? null : <Hint>{labels.scheduleHint}</Hint>}
              </FormField>
            )}
          />

          <FieldPair>
            {scheduled ? (
              <Controller
                name="publishAt"
                control={control}
                render={({ field }) => (
                  <FormField label={labels.fieldPublishAt} error={errors.publishAt?.message}>
                    <DateTimeInput
                      value={field.value}
                      ariaLabel={labels.fieldPublishAt}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      invalid={!!errors.publishAt}
                    />
                  </FormField>
                )}
              />
            ) : null}
            <Controller
              name="expiresAt"
              control={control}
              render={({ field }) => (
                <FormField label={labels.fieldExpiresAt} error={errors.expiresAt?.message}>
                  <DateTimeInput
                    value={field.value}
                    ariaLabel={labels.fieldExpiresAt}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    invalid={!!errors.expiresAt}
                  />
                </FormField>
              )}
            />
          </FieldPair>

          <Controller
            name="pinned"
            control={control}
            render={({ field }) => (
              <FormField label={labels.fieldPinned}>
                <SegmentedControl<'yes' | 'no'>
                  value={field.value ? 'yes' : 'no'}
                  onChange={value => field.onChange(value === 'yes')}
                  options={[
                    { value: 'yes', label: labels.pinnedYes },
                    { value: 'no', label: labels.pinnedNo, activeColor: t.color.muted },
                  ]}
                />
              </FormField>
            )}
          />

          <FormField label={labels.fieldImage}>
            <ImageDropzone
              file={file}
              onFile={setFile}
              currentImageUrl={currentImageUrl}
              labels={{
                drop: labels.imageDrop,
                hint: labels.imageHint,
                remove: labels.imageRemove,
                tooLarge: labels.imageTooLarge,
                invalidType: labels.imageInvalidType,
                alt: labels.fieldImage,
              }}
            />
          </FormField>
        </Body>

        <Footer>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            {labels.cancel}
          </Button>
          <Button type="submit" form="news-form" variant="primary" disabled={submitting}>
            {labels.save}
          </Button>
        </Footer>
      </Panel>
    </Drawer>
  );
};

export default NewsFormDrawer;
